import React, { createContext, useContext, useId } from 'react';

export type PresenceHexOutline = {
	/** Visible outline width beyond the center stroke (or fill) in CSS pixels. */
	width: number;
	/** Optional refined SVG viewport height in CSS pixels. */
	svgHeight?: number;
	/** Optional center stroke, kept separate from the outer outline. */
	centerStrokeColor?: string;
	centerStrokeWidth?: number;
};

export const PresenceHexOutlineContext: React.Context<PresenceHexOutline | undefined> =
	createContext<PresenceHexOutline | undefined>(undefined);

export const usePresenceSvgHeight = (size: number): number => {
	const outline = useContext(PresenceHexOutlineContext);
	return outline?.svgHeight ?? size;
};

type PresenceHexArtworkProps = {
	d: string;
	fill: string;
	stroke?: string;
	strokeWidth?: number;
	viewBox: string;
	size: number;
	/**
	 * Keeps the whole stroke inside the hexagon instead of centering it on the edge, so the
	 * visible bounding box is the same with and without the border.
	 */
	insetStroke?: boolean;
};

/**
 * Renders a theme outline behind the original fill so its inward half is covered and the fill
 * keeps its original geometry. The visible outline extends beyond the optional
 * center stroke, which is painted separately on top.
 */
export const PresenceHexArtwork = ({
	d,
	fill,
	stroke,
	strokeWidth,
	viewBox,
	size,
	insetStroke = false,
}: PresenceHexArtworkProps): React.JSX.Element => {
	const outline = useContext(PresenceHexOutlineContext);
	const clipPathId = `presence-hex-clip-${useId()}`;
	if (!outline && insetStroke && stroke && strokeWidth) {
		const insetViewBoxWidth = Number(viewBox.trim().split(/\s+/)[2]);
		const cssPixelScale =
			Number.isFinite(insetViewBoxWidth) && insetViewBoxWidth > 0 ? insetViewBoxWidth / size : 1;
		// Clipping a doubled stroke to the hexagon leaves only its inner half visible.
		return (
			<>
				<clipPath id={clipPathId}>
					<path d={d} />
				</clipPath>
				<path d={d} fill={fill} />
				<path
					data-presence-layer="inset-stroke"
					d={d}
					fill="none"
					stroke={stroke}
					strokeWidth={strokeWidth * cssPixelScale * 2}
					clipPath={`url(#${clipPathId})`}
				/>
			</>
		);
	}
	if (!outline) {
		return <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
	}

	const viewBoxWidth = Number(viewBox.trim().split(/\s+/)[2]);
	if (!Number.isFinite(viewBoxWidth) || viewBoxWidth <= 0) {
		return <path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />;
	}
	const toSvgStrokeWidth = (cssPixels: number): number => (cssPixels * viewBoxWidth) / size;
	if (outline.width <= 0) {
		return (
			<>
				<path d={d} fill={fill} stroke={stroke} strokeWidth={strokeWidth} />
				{outline.centerStrokeColor && outline.centerStrokeWidth ? (
					<path
						data-presence-layer="center-stroke"
						d={d}
						fill="none"
						stroke={outline.centerStrokeColor}
						strokeWidth={toSvgStrokeWidth(outline.centerStrokeWidth)}
					/>
				) : null}
			</>
		);
	}
	const centerStrokeWidth = outline.centerStrokeColor ? (outline.centerStrokeWidth ?? 0) : 0;
	// Half the backing stroke is covered by the fill; the center stroke covers another half-width.
	const backingStrokeWidth = toSvgStrokeWidth(outline.width * 2 + centerStrokeWidth);

	return (
		<>
			<path
				data-presence-layer="outline"
				d={d}
				fill={stroke}
				stroke={stroke}
				strokeWidth={backingStrokeWidth}
			/>
			<path data-presence-layer="fill" d={d} fill={fill} />
			{outline.centerStrokeColor && outline.centerStrokeWidth ? (
				<path
					data-presence-layer="center-stroke"
					d={d}
					fill="none"
					stroke={outline.centerStrokeColor}
					strokeWidth={toSvgStrokeWidth(outline.centerStrokeWidth)}
				/>
			) : null}
		</>
	);
};
